import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, AlertCircle, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { LogoIcon } from "@/components/Logo";

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [step, setStep] = useState("loading"); // loading | ready | done | error
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/auth/reset/${token}`)
      .then((r) => { setInfo(r.data); setStep("ready"); })
      .catch((e) => { setError(e?.response?.data?.detail || "Enlace inválido"); setStep("error"); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    // Validación cliente alineada con la nueva política (oración + dígitos + especiales)
    const SPECIAL = /[!@#$%^&*()_+\-=[\]{};:'",.<>/?\\|~`]/g;
    const lenOk = pwd.length >= 20;
    const spaceOk = (pwd.match(/ /g) || []).length >= 2;
    const digitsOk = (pwd.match(/\d/g) || []).length >= 2;
    const specialsOk = (pwd.match(SPECIAL) || []).length >= 2;
    if (!(lenOk && spaceOk && digitsOk && specialsOk)) {
      toast.error("La contraseña debe ser una oración de 20+ caracteres con al menos 3 palabras, 2 números y 2 caracteres especiales.");
      return;
    }
    if (pwd !== pwd2) { toast.error("Las contraseñas no coinciden"); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/auth/reset/${token}`, { password: pwd });
      if (setSession) setSession(r.data.token, r.data.user);
      setStep("done");
      toast.success("Contraseña actualizada");
      setTimeout(() => nav("/", { replace: true }), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo aplicar el cambio");
    } finally { setSubmitting(false); }
  };

  if (step === "loading") {
    return <div className="min-h-screen bg-[#FBFAF6] flex items-center justify-center text-[#7A786F]">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Comprobando enlace…
    </div>;
  }

  return (
    <div className="min-h-screen bg-[#FBFAF6] flex flex-col">
      <header className="px-6 py-5 flex items-center gap-2.5 border-b border-[#E5E2D8] bg-white">
        <LogoIcon size={32} />
        <div className="leading-tight">
          <div className="font-heading font-medium text-[16px] tracking-tight">
            <span className="text-[#2C2C2A]">eks</span><span className="text-[#D85A30]">vidi</span>
          </div>
          <div className="text-[10px] text-[#7A786F] tracking-wider uppercase">Restablecer contraseña</div>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md bg-white border border-[#E5E2D8] rounded-[18px] overflow-hidden p-8">
          {step === "error" && (
            <div className="text-center" data-testid="reset-error">
              <AlertCircle className="h-12 w-12 text-[#A32D2D] mx-auto mb-3" />
              <h1 className="font-heading text-2xl font-bold text-[#2C2C2A]">Enlace no válido</h1>
              <p className="text-[#7A786F] mt-2">{error}</p>
              <Button onClick={() => nav("/login")} className="btn-eks-primary rounded-[12px] mt-6" data-testid="back-login-btn">
                Volver al inicio
              </Button>
            </div>
          )}

          {step === "ready" && info && (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <h1 className="font-heading text-2xl font-bold text-[#2C2C2A]">Nueva contraseña</h1>
              <p className="text-sm text-[#7A786F]">
                Para la cuenta <b className="text-[#2C2C2A]">{info.email}</b>.
              </p>
              <div>
                <Label htmlFor="pwd">Nueva contraseña *</Label>
                <Input id="pwd" type="password" required minLength={20} value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  placeholder="Una oración con números y símbolos…"
                  className="rounded-[12px] border-[#E5E2D8]"
                  data-testid="reset-pwd-input"
                />
                <p className="text-[11px] text-[#7A786F] mt-1 leading-snug">
                  Debe ser una oración de 20+ caracteres, 3 palabras separadas por espacios, 2 números y 2 caracteres especiales.
                </p>
              </div>
              <div>
                <Label htmlFor="pwd2">Confirmar contraseña *</Label>
                <Input id="pwd2" type="password" required minLength={20} value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  className="rounded-[12px] border-[#E5E2D8]"
                  data-testid="reset-pwd2-input"
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full btn-eks-primary rounded-[12px] h-11" data-testid="reset-submit-btn">
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                Actualizar contraseña
              </Button>
            </form>
          )}

          {step === "done" && (
            <div className="text-center" data-testid="reset-done">
              <CheckCircle2 className="h-12 w-12 text-[#1D9E75] mx-auto mb-3" />
              <h1 className="font-heading text-2xl font-bold text-[#2C2C2A]">Contraseña actualizada</h1>
              <p className="text-[#7A786F] mt-2">Te estamos llevando al panel…</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
